import React, { useState } from 'react';
import OrderDetailModal from '../Order/OrderDetailModal';
import { logInfo } from '../../config_and_helpers/logger';
import './AiChatStyle.css';

const LOG_CONTEXT = 'AiChatOrderCitationsTable';

/**
 * @param {Object} citation
 * @returns {string}
 */
const getOrderRef = citation =>
  citation?.corporate_order_ref?.trim() ||
  (citation?.order_id != null ? `#${citation.order_id}` : '—');

/**
 * @param {Object} citation
 * @returns {string}
 */
const getOrderName = citation =>
  citation?.name?.trim() ||
  (citation?.order_id != null ? `Order ${citation.order_id}` : '—');

/**
 * Tabular list of order citations with Ref and Name columns.
 *
 * @param {Object} props
 * @param {Array<Object>} props.citations
 * @returns {JSX.Element|null}
 */
const AiChatOrderCitationsTable = ({ citations = [] }) => {
  const [openOrderId, setOpenOrderId] = useState(null);

  const orderCitations = citations.filter(
    citation => citation?.entity_type === 'order' && citation?.order_id
  );

  if (orderCitations.length === 0) return null;

  const openCitation = orderCitations.find(
    citation => String(citation.order_id) === String(openOrderId)
  );

  const handleRowActivate = citation => {
    logInfo(LOG_CONTEXT, 'opening order citation', {
      order_id: citation.order_id,
      corporate_order_ref: citation.corporate_order_ref,
    });
    setOpenOrderId(citation.order_id);
  };

  return (
    <>
      <table className="ai-chat-citations-table">
        <thead>
          <tr>
            <th scope="col">Ref</th>
            <th scope="col">Order</th>
          </tr>
        </thead>
        <tbody>
          {orderCitations.map((citation, idx) => {
            const ref = getOrderRef(citation);
            const name = getOrderName(citation);
            const isOpen =
              openOrderId != null &&
              String(openOrderId) === String(citation.order_id);

            return (
              <tr
                key={`order-${citation.order_id}-${idx}`}
                className={
                  isOpen
                    ? 'ai-chat-citations-table__row ai-chat-citations-table__row--active'
                    : 'ai-chat-citations-table__row'
                }
                tabIndex={0}
                role="button"
                aria-label={`Open order ${ref} ${name}`}
                onClick={() => handleRowActivate(citation)}
                onKeyDown={event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handleRowActivate(citation);
                  }
                }}
              >
                <td className="ai-chat-citations-table__primary" title={ref}>
                  {ref}
                </td>
                <td className="ai-chat-citations-table__secondary" title={name}>
                  {name}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {openCitation && (
        <div className="ai-chat-citations-table__modal-host" aria-hidden="true">
          <OrderDetailModal
            key={openCitation.order_id}
            orderId={openCitation.order_id}
            hideTrigger
            show
            onHide={() => {
              logInfo(LOG_CONTEXT, 'closing order citation modal', {
                order_id: openCitation.order_id,
              });
              setOpenOrderId(null);
            }}
          />
        </div>
      )}
    </>
  );
};

export default AiChatOrderCitationsTable;
